import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Send, Paperclip, Users, X, Shield, Trash2, Radio,
  MessageSquare, FileText, Plus, Bookmark, BookmarkCheck, Lock, ChevronRight,
  Archive as ArchiveIco,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Loading from "../components/Loading";
import AttachmentPreview from "../components/AttachmentPreview";
import ReactionBar from "../components/ReactionBar";

const MAX_UPLOAD = 25 * 1024 * 1024;

function useUpload() {
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (file.size > MAX_UPLOAD) { toast.error("FILE OVER 25 MB LIMIT"); return null; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      return r.data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "UPLOAD FAILED");
      return null;
    } finally { setUploading(false); }
  };
  return { upload, uploading };
}

function fmtTime(iso) { try { return new Date(iso).toLocaleString(); } catch { return ""; } }

/* ------------------- CHAT TAB ------------------- */
function ChatTab({ cid, canPost, currentUser, isAdmin, archived }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [pending, setPending] = useState(null); // uploaded attachment
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const { upload, uploading } = useUpload();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/communities/${cid}/messages?limit=200`);
      setMsgs(r.data.results || []);
    } catch (err) { toast.error(err?.response?.data?.detail || "LOAD FAILED"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cid]);
  useEffect(() => { const el = scrollRef.current; if (el) el.scrollTop = el.scrollHeight; }, [msgs.length, loading]);

  const onFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
    const u = await upload(f);
    if (u) setPending(u);
  };

  const send = async (e) => {
    e?.preventDefault();
    const body = text.trim();
    if (!body && !pending) return;
    setBusy(true);
    try {
      const r = await api.post(`/communities/${cid}/messages`, {
        body, type: "message", attachment_id: pending?.id || null,
      });
      setMsgs(m => [...m, r.data]);
      setText(""); setPending(null);
    } catch (err) { toast.error(err?.response?.data?.detail || "SEND FAILED"); }
    finally { setBusy(false); }
  };

  const delMsg = async (mid) => {
    if (!window.confirm("Delete this transmission?")) return;
    try {
      await api.delete(`/communities/${cid}/messages/${mid}`);
      setMsgs(m => m.filter(x => x.id !== mid));
    } catch (err) { toast.error(err?.response?.data?.detail || "DELETE FAILED"); }
  };

  const updateReactions = (mid, next) => {
    setMsgs(cur => cur.map(m => m.id === mid ? { ...m, reactions: next } : m));
  };

  return (
    <div className="flex-1 sat-surface border border-[color:var(--sat-border-bright)] flex flex-col min-h-0">
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3" data-testid="community-messages">
        {loading && <div className="sat-label text-[color:var(--sat-text-dim)] text-center py-8">LOADING…</div>}
        {!loading && msgs.length === 0 && (
          <div className="sat-label text-[color:var(--sat-text-dim)] py-8 text-center" data-testid="community-messages-empty">
            <Radio size={14} className="inline mr-1" /> NO TRANSMISSIONS YET
          </div>
        )}
        {msgs.map(m => {
          const mine = currentUser && m.user_id === currentUser.id;
          return (
            <div key={m.id} className={`flex flex-col ${mine ? "items-end" : "items-start"}`} data-testid={`msg-${m.id}`}>
              <div className="flex items-center gap-2 mb-1 text-[10px] tracking-widest font-mono text-[color:var(--sat-text-dim)]">
                <span className="sat-cyan" data-testid={`msg-callsign-${m.id}`}>{m.callsign}</span>
                <span>·</span>
                <span>{fmtTime(m.created_at)}</span>
                {(mine || isAdmin) && (
                  <button onClick={() => delMsg(m.id)} data-testid={`msg-delete-${m.id}`}
                    className="ml-1 text-[color:var(--sat-text-dim)] hover:text-red-400" title="Delete">
                    <Trash2 size={10} />
                  </button>
                )}
              </div>
              <div className={`max-w-[85%] border p-2.5 space-y-2 ${mine ? "border-[color:var(--sat-cyan)]" : "border-[color:var(--sat-border)]"}`}>
                {m.attachment && <AttachmentPreview attachment={m.attachment} compact />}
                {m.body && <div className="text-sm whitespace-pre-wrap break-words">{m.body}</div>}
                <ReactionBar cid={cid} mid={m.id} reactions={m.reactions} currentUserId={currentUser?.id}
                  onChange={(next) => updateReactions(m.id, next)} disabled={archived} />
              </div>
            </div>
          );
        })}
      </div>
      {archived ? (
        <div className="border-t border-[color:var(--sat-border)] p-3 sat-label text-[color:var(--sat-text-dim)] text-center" data-testid="community-archived-banner">
          <Lock size={11} className="inline mr-1" /> GROUP ARCHIVED — POSTING DISABLED
        </div>
      ) : canPost ? (
        <form onSubmit={send} className="border-t border-[color:var(--sat-border)] p-2 space-y-2">
          {pending && (
            <div className="relative inline-flex items-center gap-2 border border-[color:var(--sat-cyan)] p-2" data-testid="community-pending-attachment">
              <AttachmentPreview attachment={pending} compact />
              <button type="button" data-sfx-silent onClick={() => setPending(null)}
                className="absolute -top-2 -right-2 border border-[color:var(--sat-cyan)] bg-black p-0.5" aria-label="Remove attachment">
                <X size={10} />
              </button>
            </div>
          )}
          <div className="flex gap-2">
            <input ref={fileRef} type="file" className="hidden" onChange={onFile} data-testid="community-file-input" />
            <button type="button" onClick={() => fileRef.current?.click()} data-testid="community-attach-btn"
              className="sat-btn !py-2 !px-2 disabled:opacity-40" disabled={uploading} title="Attach a file (≤25 MB)">
              <Paperclip size={12} />
            </button>
            <input data-testid="community-input" value={text} onChange={(e) => setText(e.target.value)}
              placeholder="Send a transmission…" maxLength={2000}
              className="flex-1 bg-black border border-[color:var(--sat-border)] focus:border-[color:var(--sat-cyan)] outline-none px-2 py-2 font-mono text-sm" />
            <button type="submit" disabled={busy || uploading || (!text.trim() && !pending)}
              data-testid="community-send" className="sat-btn sat-btn-solid !py-2 !px-3 disabled:opacity-40">
              <Send size={12} />
            </button>
          </div>
          {uploading && <div className="sat-label text-[color:var(--sat-text-dim)]">UPLOADING…</div>}
        </form>
      ) : (
        <div className="border-t border-[color:var(--sat-border)] p-3 sat-label text-[color:var(--sat-text-dim)] text-center" data-testid="community-must-join">
          {currentUser ? "JOIN THIS COMMUNITY TO POST" : "LOG IN AND JOIN TO POST"}
        </div>
      )}
    </div>
  );
}

/* ------------------- POST COMPOSER ------------------- */
function PostComposer({ cid, onCreated }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const { upload, uploading } = useUpload();
  const fileRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/communities/${cid}/messages`, {
        type: "post", title, body, attachment_id: pending?.id || null,
      });
      onCreated(r.data);
      setTitle(""); setBody(""); setPending(null); setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "POST FAILED");
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid="posts-new-btn" className="sat-btn sat-btn-solid">
        <Plus size={12} /> NEW POST
      </button>
    );
  }
  return (
    <form onSubmit={submit} className="sat-surface sat-brackets border border-[color:var(--sat-cyan)] p-4 space-y-3" data-testid="posts-composer">
      <div className="sat-label sat-cyan">NEW POST</div>
      <input data-testid="posts-title" value={title} onChange={(e) => setTitle(e.target.value)}
        placeholder="POST TITLE" maxLength={120} required
        className="w-full bg-black border border-[color:var(--sat-border)] focus:border-[color:var(--sat-cyan)] outline-none px-3 py-2 font-mono text-sm" />
      <textarea data-testid="posts-body" value={body} onChange={(e) => setBody(e.target.value)}
        placeholder="What's on your mind, pilot?" rows={4} maxLength={2000}
        className="w-full bg-black border border-[color:var(--sat-border)] focus:border-[color:var(--sat-cyan)] outline-none px-3 py-2 font-mono text-sm resize-none" />
      {pending && (
        <div className="relative inline-flex items-center gap-2 border border-[color:var(--sat-cyan)] p-2" data-testid="posts-pending-attachment">
          <AttachmentPreview attachment={pending} compact />
          <button type="button" data-sfx-silent onClick={() => setPending(null)}
            className="absolute -top-2 -right-2 border border-[color:var(--sat-cyan)] bg-black p-0.5">
            <X size={10} />
          </button>
        </div>
      )}
      <input ref={fileRef} type="file" className="hidden" data-testid="posts-file-input" onChange={async (e) => {
        const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
        const u = await upload(f); if (u) setPending(u);
      }} />
      <div className="flex items-center justify-between gap-2">
        <button type="button" onClick={() => fileRef.current?.click()} data-testid="posts-attach-btn"
          className="sat-btn !py-2 !px-2 disabled:opacity-40" disabled={uploading}>
          <Paperclip size={11} /> {pending ? "REPLACE" : "ATTACH"}
        </button>
        <div className="flex gap-2">
          <button type="button" onClick={() => setOpen(false)} className="sat-btn !py-2 !px-3">CANCEL</button>
          <button type="submit" disabled={busy || uploading || !title.trim()} data-testid="posts-submit"
            className="sat-btn sat-btn-solid !py-2 !px-4 disabled:opacity-40">
            {busy ? "TRANSMITTING…" : "POST"}
          </button>
        </div>
      </div>
    </form>
  );
}

/* ------------------- POST COMMENTS ------------------- */
function PostThread({ cid, post, currentUser, isAdmin, archived, onDelete, onReactChange }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const { upload, uploading } = useUpload();
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/communities/${cid}/messages?parent_id=${post.id}&limit=200`);
      setComments(r.data.results || []);
    } catch { /* silent */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [post.id]);

  const send = async (e) => {
    e?.preventDefault();
    const body = text.trim();
    if (!body && !pending) return;
    setBusy(true);
    try {
      const r = await api.post(`/communities/${cid}/messages`, {
        body, type: "message", attachment_id: pending?.id || null, parent_id: post.id,
      });
      setComments(c => [...c, r.data]);
      setText(""); setPending(null);
    } catch (err) { toast.error(err?.response?.data?.detail || "REPLY FAILED"); }
    finally { setBusy(false); }
  };

  const del = async (mid) => {
    if (!window.confirm("Delete this reply?")) return;
    try {
      await api.delete(`/communities/${cid}/messages/${mid}`);
      setComments(c => c.filter(x => x.id !== mid));
    } catch (err) { toast.error(err?.response?.data?.detail || "DELETE FAILED"); }
  };

  const updateCommentReactions = (mid, next) => {
    setComments(cur => cur.map(m => m.id === mid ? { ...m, reactions: next } : m));
  };

  return (
    <article className="sat-brackets sat-surface border border-[color:var(--sat-border-bright)] p-4 space-y-3" data-testid={`post-${post.id}`}>
      <header className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="sat-label sat-cyan">POST · @{post.callsign}</div>
          <h3 className="font-display font-bold text-lg mt-0.5" data-testid={`post-title-${post.id}`}>{post.title}</h3>
          <div className="sat-label mt-0.5 text-[color:var(--sat-text-dim)]">{fmtTime(post.created_at)}</div>
        </div>
        {(currentUser && (post.user_id === currentUser.id || isAdmin)) && (
          <button onClick={() => onDelete(post.id)} data-testid={`post-delete-${post.id}`}
            className="sat-btn !py-1 !px-2" title="Delete post">
            <Trash2 size={11} />
          </button>
        )}
      </header>
      {post.attachment && <AttachmentPreview attachment={post.attachment} />}
      {post.body && <div className="text-sm whitespace-pre-wrap leading-relaxed">{post.body}</div>}
      <ReactionBar cid={cid} mid={post.id} reactions={post.reactions} currentUserId={currentUser?.id}
        onChange={(next) => onReactChange(post.id, next)} disabled={archived} />

      <div className="border-t border-[color:var(--sat-border)] pt-3 space-y-2">
        <div className="sat-label sat-cyan flex items-center gap-1">
          <MessageSquare size={11} /> COMMENTS · {comments.length}
        </div>
        {loading && <div className="sat-label text-[color:var(--sat-text-dim)]">LOADING…</div>}
        {!loading && comments.length === 0 && (
          <div className="sat-label text-[color:var(--sat-text-dim)]" data-testid={`comments-empty-${post.id}`}>
            NO REPLIES YET
          </div>
        )}
        <ul className="space-y-2" data-testid={`comments-${post.id}`}>
          {comments.map(c => (
            <li key={c.id} className="border-l-2 border-[color:var(--sat-border)] pl-3 py-1" data-testid={`comment-${c.id}`}>
              <div className="text-[10px] font-mono text-[color:var(--sat-text-dim)] flex items-center gap-2">
                <span className="sat-cyan">@{c.callsign}</span>
                <span>·</span>
                <span>{fmtTime(c.created_at)}</span>
                {(currentUser && (c.user_id === currentUser.id || isAdmin)) && (
                  <button onClick={() => del(c.id)} data-testid={`comment-delete-${c.id}`}
                    className="hover:text-red-400"><Trash2 size={9} /></button>
                )}
              </div>
              {c.attachment && <div className="mt-1"><AttachmentPreview attachment={c.attachment} compact /></div>}
              {c.body && <div className="text-sm mt-1 whitespace-pre-wrap break-words">{c.body}</div>}
              <div className="mt-1">
                <ReactionBar cid={cid} mid={c.id} reactions={c.reactions} currentUserId={currentUser?.id}
                  onChange={(next) => updateCommentReactions(c.id, next)} disabled={archived} />
              </div>
            </li>
          ))}
        </ul>
        {archived ? (
          <div className="sat-label text-[color:var(--sat-text-dim)]"><Lock size={10} className="inline mr-1" /> ARCHIVED</div>
        ) : currentUser ? (
          <form onSubmit={send} className="flex flex-col gap-2">
            {pending && (
              <div className="relative inline-flex items-center gap-2 border border-[color:var(--sat-cyan)] p-2" data-testid={`comment-pending-${post.id}`}>
                <AttachmentPreview attachment={pending} compact />
                <button type="button" data-sfx-silent onClick={() => setPending(null)}
                  className="absolute -top-2 -right-2 border border-[color:var(--sat-cyan)] bg-black p-0.5"><X size={10} /></button>
              </div>
            )}
            <div className="flex gap-2">
              <input ref={fileRef} type="file" className="hidden" data-testid={`comment-file-${post.id}`}
                onChange={async (e) => {
                  const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
                  const u = await upload(f); if (u) setPending(u);
                }} />
              <button type="button" onClick={() => fileRef.current?.click()} data-testid={`comment-attach-${post.id}`}
                className="sat-btn !py-1 !px-2 disabled:opacity-40" disabled={uploading}>
                <Paperclip size={10} />
              </button>
              <input value={text} onChange={(e) => setText(e.target.value)}
                data-testid={`comment-input-${post.id}`}
                placeholder="Reply…" maxLength={2000}
                className="flex-1 bg-black border border-[color:var(--sat-border)] focus:border-[color:var(--sat-cyan)] outline-none px-2 py-1 font-mono text-xs" />
              <button type="submit" disabled={busy || uploading || (!text.trim() && !pending)}
                data-testid={`comment-send-${post.id}`}
                className="sat-btn sat-btn-solid !py-1 !px-2 disabled:opacity-40">
                <Send size={10} />
              </button>
            </div>
          </form>
        ) : (
          <div className="sat-label text-[color:var(--sat-text-dim)]">LOG IN TO REPLY</div>
        )}
      </div>
    </article>
  );
}

/* ------------------- POSTS TAB ------------------- */
function PostsTab({ cid, canPost, currentUser, isAdmin, archived }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/communities/${cid}/messages?type=post&limit=200`);
      const results = (r.data.results || []).slice().reverse();
      setPosts(results);
    } catch (err) { toast.error(err?.response?.data?.detail || "POSTS LOAD FAILED"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cid]);

  const del = async (pid) => {
    if (!window.confirm("Delete this post and all its replies?")) return;
    try {
      await api.delete(`/communities/${cid}/messages/${pid}`);
      setPosts(p => p.filter(x => x.id !== pid));
    } catch (err) { toast.error(err?.response?.data?.detail || "DELETE FAILED"); }
  };

  const updateReactions = (mid, next) => {
    setPosts(cur => cur.map(m => m.id === mid ? { ...m, reactions: next } : m));
  };

  return (
    <div className="space-y-4" data-testid="posts-list">
      {canPost && !archived && (
        <PostComposer cid={cid} onCreated={(p) => setPosts(cur => [p, ...cur])} />
      )}
      {loading && <Loading message="LOADING POSTS" />}
      {!loading && posts.length === 0 && (
        <div className="sat-label text-[color:var(--sat-text-dim)] text-center py-6" data-testid="posts-empty">
          NO POSTS YET — {canPost && !archived ? "OPEN THE FIRST THREAD." : "JOIN TO CONTRIBUTE."}
        </div>
      )}
      {posts.map(p => (
        <div key={p.id}>
          <button
            onClick={() => setExpanded(e => e === p.id ? null : p.id)}
            data-testid={`post-toggle-${p.id}`}
            className={`w-full text-left sat-brackets sat-surface border p-3 flex items-center justify-between gap-3 hover:border-[color:var(--sat-cyan)] transition-colors ${expanded === p.id ? "border-[color:var(--sat-cyan)]" : "border-[color:var(--sat-border-bright)]"}`}
          >
            <div className="flex-1 min-w-0">
              <div className="sat-label sat-cyan mb-0.5">POST · @{p.callsign} · {fmtTime(p.created_at)}</div>
              <div className="font-display font-bold text-base truncate">{p.title}</div>
              <div className="sat-label text-[color:var(--sat-text-dim)] mt-1 flex items-center gap-3">
                <span className="flex items-center gap-1"><MessageSquare size={10} /> {p.reply_count || 0}</span>
                {p.attachment && <span className="flex items-center gap-1"><Paperclip size={10} /> ATTACHED</span>}
                {Object.keys(p.reactions || {}).length > 0 && (
                  <span>REACTIONS · {Object.values(p.reactions).reduce((s, u) => s + u.length, 0)}</span>
                )}
              </div>
            </div>
            <ChevronRight size={14} className={`transition-transform ${expanded === p.id ? "rotate-90 sat-cyan" : ""}`} />
          </button>
          {expanded === p.id && (
            <div className="mt-3">
              <PostThread cid={cid} post={p} currentUser={currentUser} isAdmin={isAdmin}
                archived={archived} onDelete={del} onReactChange={updateReactions} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ------------------- MAIN PAGE ------------------- */
export default function CommunityDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("chat");
  const [showMembers, setShowMembers] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const m = await api.get(`/communities/${id}`);
      setMeta(m.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "COMMUNITY NOT FOUND");
      nav("/community", { replace: true });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const join = async () => {
    try { await api.post(`/communities/${id}/join`); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "JOIN FAILED"); }
  };

  const togglePersonalArchive = async () => {
    if (!user) { toast.error("LOG IN TO ARCHIVE"); return; }
    try {
      if (meta.archived_by_me) {
        await api.delete(`/communities/${id}/archive`);
        toast.success("RESTORED TO LIST");
      } else {
        await api.post(`/communities/${id}/archive`);
        toast.success("SAVED TO ARCHIVE");
      }
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "ARCHIVE FAILED"); }
  };

  const toggleGroupArchive = async () => {
    if (!window.confirm(meta.archived ? "Reopen this group for posting?" : "Archive this group? Posting will be frozen for everyone.")) return;
    try {
      if (meta.archived) await api.delete(`/communities/${id}/archive-group`);
      else await api.post(`/communities/${id}/archive-group`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "OPERATION FAILED"); }
  };

  if (loading) return <Loading message="OPENING TRANSMISSION" />;
  if (!meta) return null;

  const isAdmin = !!user?.is_admin;
  const canPost = meta.is_member || isAdmin;

  return (
    <div data-testid="page-community-detail" className="flex flex-col min-h-[calc(100vh-260px)]">
      {/* Header */}
      <div className="border-b border-[color:var(--sat-border)] pb-3 mb-3 flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <Link to="/community" className="sat-label inline-flex items-center gap-2 mb-1 hover:sat-cyan" data-testid="community-back">
            <ArrowLeft size={12} /> BACK
          </Link>
          <h1 className="font-display font-bold text-2xl md:text-3xl flex items-center gap-2 flex-wrap">
            {meta.name}
            {meta.archived && (
              <span className="sat-label border border-[color:var(--sat-live)] text-[color:var(--sat-live)] px-2 py-0.5 inline-flex items-center gap-1" data-testid="community-archived-tag">
                <Lock size={9} /> ARCHIVED
              </span>
            )}
          </h1>
          {meta.description && <p className="text-sm text-[color:var(--sat-text-dim)] mt-1 max-w-2xl">{meta.description}</p>}
          <div className="sat-label mt-1 text-[color:var(--sat-text-dim)]">CREATED BY @{meta.created_by}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setShowMembers(v => !v)} data-testid="community-members-btn"
            className={`sat-btn !py-2 !px-3 ${showMembers ? "sat-btn-solid" : ""}`}>
            <Users size={11} /> {meta.member_count}
          </button>
          {user && (
            <button onClick={togglePersonalArchive} data-testid="community-personal-archive"
              title={meta.archived_by_me ? "Restore to list" : "Save/archive (hidden from list)"}
              className="sat-btn !py-2 !px-3">
              {meta.archived_by_me ? <><BookmarkCheck size={11} /> SAVED</> : <><Bookmark size={11} /> SAVE</>}
            </button>
          )}
          {isAdmin && (
            <button onClick={toggleGroupArchive} data-testid="community-group-archive"
              className={`sat-btn !py-2 !px-3 ${meta.archived ? "sat-btn-solid" : ""}`}>
              <ArchiveIco size={11} /> {meta.archived ? "REOPEN" : "ARCHIVE"}
            </button>
          )}
          {!meta.is_member && user && !meta.archived && (
            <button onClick={join} data-testid="community-join-btn" className="sat-btn sat-btn-solid !py-2 !px-3">JOIN</button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-3" data-testid="community-tabs">
        {[["chat", "CHAT", MessageSquare], ["posts", "POSTS", FileText]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`community-tab-${k}`}
            className={`sat-btn !py-2 !px-3 ${tab === k ? "sat-btn-solid" : ""}`}>
            <Icon size={11} /> {label}
          </button>
        ))}
      </div>

      <div className="flex-1 flex gap-3 min-h-0">
        <div className="flex-1 min-w-0 flex flex-col">
          {tab === "chat" ? (
            <div className="flex-1 flex min-h-[500px]">
              <ChatTab cid={id} canPost={canPost} currentUser={user} isAdmin={isAdmin} archived={meta.archived} />
            </div>
          ) : (
            <PostsTab cid={id} canPost={canPost} currentUser={user} isAdmin={isAdmin} archived={meta.archived} />
          )}
        </div>

        {showMembers && (
          <aside className="w-64 sat-surface border border-[color:var(--sat-border-bright)] p-3 overflow-y-auto" data-testid="community-members-list">
            <div className="sat-label sat-cyan mb-2 flex items-center gap-1"><Users size={11} /> MEMBERS · {meta.member_count}</div>
            <ul className="space-y-1">
              {meta.members?.map(m => (
                <li key={m.user_id} className="text-[11px] font-mono text-[color:var(--sat-text)] flex items-center gap-1"
                  data-testid={`member-${m.user_id}`}>
                  {m.role === "admin" && <Shield size={9} className="sat-cyan" />}
                  <span className="sat-cyan">@{m.callsign}</span>
                  <span className="text-[color:var(--sat-text-dim)] text-[9px]">· {m.role}</span>
                </li>
              ))}
            </ul>
          </aside>
        )}
      </div>
    </div>
  );
}
